import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Check, ArrowUpRight, Sparkles, ShieldCheck, ClipboardCheck, Layers, GraduationCap, Loader2, Lock } from "lucide-react";
import { fetchMe } from "@/lib/api";

const TIERS = [
  {
    name: "Verification Pathway",
    subtitle: "Low risk providers",
    price: "$2,500 – $4,500",
    note: "AUD · scoped per engagement",
    for: "Therapeutic supports, community transport, standard home cleaning and other lower risk services.",
    features: [
      "Tailored core module policies",
      "Participant intake forms",
      "Incident registers",
      "Human resources documents",
      "Pre-audit desk review",
    ],
    cta: "Enquire about Verification",
  },
  {
    name: "Certification Pathway",
    subtitle: "High risk providers",
    price: "$4,500 – $8,000",
    note: "AUD · scoped per engagement",
    for: "High intensity daily personal activities, group homes, early childhood intervention and other higher risk services.",
    features: [
      "All core module documentation",
      "Complex risk management framework",
      "Infection control policies",
      "Restrictive practice policies",
      "Pre-audit review and mock interviews",
      "Implementation walkthrough",
    ],
    cta: "Enquire about Certification",
    featured: true,
  },
  {
    name: "Complex Module Add ons",
    subtitle: "Specialist modules",
    price: "+$1,000 – $2,000",
    note: "AUD · per module",
    for: "Additional specialised policy frameworks for SDA, behaviour support plans, emergency disaster management and other specialist modules.",
    features: [
      "Module specific policy framework",
      "Tailored evidence library",
      "Practice Standard mapping",
      "Add on to either pathway",
    ],
    cta: "Enquire about add ons",
  },
];

const INCLUDED = [
  { icon: Layers,         t: "Tailored mapping",       d: "Policies linked directly to your business model, staff structure and specific state regulations." },
  { icon: ClipboardCheck, t: "Pre audit review",       d: "Documentation is reviewed for readiness before your stage one desk audit." },
  { icon: GraduationCap,  t: "Implementation training",d: "A walkthrough session so you and your team understand registers, logs and feedback loops in daily operations." },
];

const PLAN_OPTIONS = [
  { n: "1", label: "Pay in full", desc: "Best for straightforward engagements. Documentation released once received." },
  { n: "2", label: "2 instalments", desc: "Split into two payments. Documentation released once the final instalment clears." },
  { n: "3", label: "3 instalments", desc: "Spread across three payments. Documentation released once the final instalment clears." },
];

export default function Pricing() {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "checking" });

  useEffect(() => {
    fetchMe()
      .then((u) => setState({ status: "ok", user: u }))
      .catch(() => setState({ status: "unauth" }));
  }, []);

  if (state.status === "checking") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 size={28} className="text-teal animate-spin" />
      </div>
    );
  }

  if (state.status === "unauth") {
    return (
      <div data-testid="pricing-gate" className="container-pad py-24">
        <div className="card-soft p-12 max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-brand-gradient text-canvas flex items-center justify-center">
            <Lock size={28} />
          </div>
          <div className="overline mt-8">Client pricing</div>
          <h1 className="font-display text-4xl text-navy mt-3 tracking-tight">
            Pricing is shared after your Discovery consultation.
          </h1>
          <p className="text-muted2 mt-4 leading-relaxed">
            Every engagement is scoped and quoted individually. Book a free Discovery call and we'll walk you through
            the right pathway, the exact scope, and the transparent pricing tailored to your organisation.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact" data-testid="pricing-book-discovery" className="btn-ochre">
              Book free Discovery <ArrowUpRight size={16} />
            </Link>
            <button onClick={() => navigate("/login")} data-testid="pricing-signin" className="btn-ghost-navy">
              I'm a client, sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pricing-page">
      <section className="container-pad pt-16 pb-12">
        <div className="overline">Pricing</div>
        <h1 className="font-display text-5xl sm:text-6xl text-navy mt-6 tracking-tight max-w-4xl leading-[1.05]">
          Transparent pricing for <span className="text-brand-gradient font-serif-mark italic">consultant prepared</span> policy work.
        </h1>
        <p className="text-muted2 mt-8 max-w-2xl leading-relaxed text-lg">
          Every engagement is scoped and quoted before you commit. Prices below reflect the standard market for
          consultant prepared NDIS registration documentation.
        </p>
      </section>

      <section className="container-pad pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TIERS.map((tier, idx) => (
            <div key={tier.name} data-testid={`tier-${idx}`}
              className={`relative p-10 flex flex-col rounded-3xl transition-all duration-300 ${
                tier.featured
                  ? "bg-brand-gradient text-canvas shadow-2xl shadow-teal/20 md:scale-[1.03]"
                  : "card-soft"
              }`}>
              {tier.featured && (
                <div className="absolute -top-3 left-10 bg-canvas text-navy text-[10px] tracking-[0.22em] uppercase font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1 shadow-md">
                  <Sparkles size={12} className="text-teal" /> Most common
                </div>
              )}
              <div className="overline" style={tier.featured ? { color: '#A7E0E6' } : undefined}>{tier.subtitle}</div>
              <div className={`font-display text-3xl mt-3 ${tier.featured ? "text-canvas" : "text-navy"}`}>{tier.name}</div>
              <div className="mt-6">
                <span className={`font-display text-4xl ${tier.featured ? "text-canvas" : "text-navy"}`}>{tier.price}</span>
                <div className={`text-xs uppercase tracking-widest mt-2 ${tier.featured ? "text-canvas/60" : "text-muted2"}`}>{tier.note}</div>
              </div>
              <p className={`mt-5 text-sm leading-relaxed ${tier.featured ? "text-canvas/80" : "text-muted2"}`}>{tier.for}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <Check size={16} className={tier.featured ? "text-canvas mt-0.5" : "text-teal mt-0.5"} />
                    <span className={tier.featured ? "text-canvas/90" : "text-ink"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" data-testid={`tier-cta-${idx}`}
                    className={`mt-10 justify-center ${tier.featured ? "btn-light-on-navy" : "btn-ghost-navy"}`}>
                {tier.cta} <ArrowUpRight size={16} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="bg-sand py-24">
        <div className="container-pad">
          <div className="overline">What's included</div>
          <h2 className="font-display text-3xl sm:text-4xl text-navy mt-4 max-w-2xl tracking-tight leading-[1.08]">
            What you get for consultant prepared work.
          </h2>
          <p className="text-muted2 mt-4 max-w-2xl leading-relaxed">
            Every Elevate engagement extends well beyond a standard download link.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
            {INCLUDED.map((i) => (
              <div key={i.t} className="card-soft p-8">
                <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center">
                  <i.icon size={22} className="text-teal" />
                </div>
                <div className="font-display text-xl text-navy mt-5">{i.t}</div>
                <p className="text-muted2 mt-3 text-sm leading-relaxed">{i.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PAYMENT PLANS */}
      <section className="container-pad py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline">Payment plans</div>
            <h2 className="font-display text-3xl sm:text-4xl text-navy mt-4 tracking-tight leading-[1.08]">
              Split the total. Documentation released once paid in full.
            </h2>
            <p className="text-muted2 mt-6 leading-relaxed">
              Payment plans are available on every engagement. Your consultant will scope the total in Discovery and
              set up your instalments in the portal. Documentation is only released once the final instalment clears.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 bg-teal/10 border border-teal/30 rounded-2xl px-5 py-4">
              <ShieldCheck size={18} className="text-teal" />
              <span className="text-sm text-ink">Secure payments through Stripe. Cards, Apple Pay and Google Pay supported.</span>
            </div>
          </div>
          <div className="md:col-span-7 space-y-4">
            {PLAN_OPTIONS.map((p) => (
              <div key={p.n} className="card-soft p-6 flex items-start gap-5">
                <div className="w-12 h-12 rounded-full bg-brand-gradient text-canvas font-display flex items-center justify-center flex-shrink-0">
                  {p.n}
                </div>
                <div>
                  <div className="font-display text-lg text-navy">{p.label}</div>
                  <p className="text-muted2 mt-1 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-pad pb-24">
        <div className="bg-brand-gradient text-canvas rounded-3xl p-12 md:p-16 grid md:grid-cols-12 gap-6 items-center grain relative overflow-hidden">
          <div className="md:col-span-8">
            <div className="overline" style={{ color: '#A7E0E6' }}>Ready to scope your engagement?</div>
            <h3 className="font-display text-3xl mt-3 tracking-tight">Book a free Discovery call. We'll quote your work honestly.</h3>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" data-testid="pricing-cta" className="btn-light-on-navy">
              Book Discovery <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
